<div class="forgot-password-container">
  <div class="wrapper">
    <!-- LEFT SIDE -->
    <section [class]="loginLeftCssClass" [innerHTML]="loginLeftBoxHtml">
    </section>

    <!-- RIGHT SIDE -->
    <section class="forgot-password-panel login-panel">
      <div class="forgot-password-card login-card">
        <div class="forgot-password-header login-header hide">
          <h2>Reset Password</h2>
          <p>Enter your username and verification code to reset your password</p>
        </div>

        <!-- Success Message -->
        <div *ngIf="successMessage" class="alert alert-success" role="alert">
          <mat-icon fontSet="material-symbols-rounded" class="alert-icon">check_circle</mat-icon>
          {{ successMessage }}
        </div>

        <!-- Error Message -->
        <div *ngIf="errorMessage" class="alert alert-danger" role="alert">
          <mat-icon fontSet="material-symbols-rounded" class="alert-icon">error</mat-icon>
          <div class="alert-message">
            <div *ngFor="let line of errorMessage.split('\n')">{{ line }}</div>
          </div>
        </div>

        <form class="forgot-password-form login-form" [formGroup]="forgotPasswordForm" (ngSubmit)="onSubmit()">

          <!-- STEP 1: Username Entry -->
          <div class="form-section">
            <h3 class="section-title">Step 1: Verify Username</h3>

            <!-- Username Field -->
            <div class="">
              <div class="input-group">
                <span class="input-icon">
                  <mat-icon fontSet="material-symbols-rounded">person</mat-icon>
                </span>
                <input id="username"
                       type="text"
                       placeholder="Enter your username"
                       formControlName="userName"
                       (blur)="onUsernameBlur()"
                       [class.is-invalid]="userName?.invalid && userName?.touched"
                       [disabled]="isVerificationCodeAvailable()" />
              </div>
              <!-- Username checking spinner -->
              <div *ngIf="isCheckingUsername" class="spinner-small">
                <mat-spinner [diameter]="20" [strokeWidth]="2"></mat-spinner>
              </div>
              <!-- Username validation icons/messages -->
              <div *ngIf="!isCheckingUsername && userFound" class="validation-icon success small">
                <mat-icon fontSet="material-symbols-rounded">check_circle</mat-icon>
                User found. Verification code will be sent to the user's email.
              </div>
              <div *ngIf="!isCheckingUsername && usernameNotFound" class="validation-icon error-strip small">
                <mat-icon fontSet="material-symbols-rounded">cancel</mat-icon>
                User not found
              </div>
              <div *ngIf="userName?.invalid && userName?.touched" class="text-danger small">
                <div *ngIf="userName?.errors?.['required']">Username is required</div>
                <div *ngIf="userName?.errors?.['minlength']">Username must be at least 3 characters</div>
              </div>
            </div>

            <!-- Generate Verification Code Button -->
            <button type="button"
                    class="generate-code-btn"
                    (click)="onGenerateVerificationCode()"
                    [disabled]="isGeneratingCode || !userName?.value || userName?.invalid || isVerificationCodeAvailable() || !userFound">
              <span *ngIf="!isGeneratingCode">Generate Verification Code</span>
              <span *ngIf="isGeneratingCode">
                <i class="fas fa-spinner fa-spin"></i> Generating...
              </span>
            </button>
          </div>

          <!-- STEP 2: Verification Code Entry with Timer -->
          <div class="form-section" *ngIf="isVerificationCodeAvailable() || timerSeconds > 0">
            <div class="step-header">
              <h3 class="section-title">Step 2: Enter Verification Code</h3>
              <div class="timer-badge" [class.expired]="isVerificationCodeExpired">
                <mat-icon fontSet="material-symbols-rounded">schedule</mat-icon>
                <span>{{ timerDisplay }}</span>
              </div>
            </div>

            <!-- Timer Status Message -->
            <div *ngIf="!isVerificationCodeExpired && timerSeconds > 0" class="timer-status info">
              <mat-icon fontSet="material-symbols-rounded">info</mat-icon>
              <span>Verification code expires in {{ timerMinutes }}:{{ (timerSeconds % 60) | string:".2-2" }}</span>
            </div>

            <!-- Verification Code Field -->
            <div class="">
              <div class="input-group">
                <span class="input-icon">
                  <mat-icon fontSet="material-symbols-rounded">verified_user</mat-icon>
                </span>
                <input id="verificationCode"
                       type="text"
                       placeholder="Enter verification code"
                       formControlName="verificationCode"
                       [class.is-invalid]="verificationCode?.invalid && verificationCode?.touched"
                       [disabled]="!isVerificationCodeAvailable() || isVerificationCodeExpired" />
              </div>
              <div *ngIf="verificationCode?.invalid && verificationCode?.touched" class="text-danger small">
                <div *ngIf="verificationCode?.errors?.['required']">Verification code is required</div>
                <div *ngIf="verificationCode?.errors?.['minlength']">Verification code must be at least 4 characters</div>
              </div>
            </div>
          </div>

          <!-- STEP 3: New Password Entry -->
          <div class="form-section no-border" *ngIf="isVerificationCodeAvailable()">
            <h3 class="section-title">Step 3: Set New Password</h3>

            <!-- New Password Field -->
            <div class="">
              <div class="input-group">
                <span class="input-icon">
                  <mat-icon fontSet="material-symbols-rounded">lock</mat-icon>
                </span>
                <input id="newPassword"
                       [type]="showPassword ? 'text' : 'password'"
                       placeholder="Enter new password"
                       formControlName="newPassword"
                       [class.is-invalid]="newPassword?.invalid && newPassword?.touched"
                       [disabled]="!isVerificationCodeAvailable() || isVerificationCodeExpired" />
                <button type="button"
                        class="eye-btn"
                        (click)="togglePasswordVisibility()"
                        tabindex="-1"
                        [disabled]="!isVerificationCodeAvailable() || isVerificationCodeExpired">
                  <mat-icon fontSet="material-symbols-rounded">
                    {{ showPassword ? 'visibility' : 'visibility_off' }}
                  </mat-icon>
                </button>
              </div>
              <div *ngIf="newPassword?.invalid && newPassword?.touched" class="text-danger small">
                <div *ngIf="newPassword?.errors?.['required']">New password is required</div>
                <div *ngIf="newPassword?.errors?.['minlength']" class="hide" >Password must be at least 6 characters</div>
                <div *ngIf="newPassword?.errors?.['passwordStrength']">
                  {{ getErrorMessage('newPassword') }}
                </div>
              </div>
              <div class="password-hint small hide">
                <mat-icon fontSet="material-symbols-rounded" class="inline-icon">info</mat-icon>
                Password must contain at least one uppercase letter and one digit
              </div>
            </div>

            <!-- Confirm Password Field -->
            <div class="">
              <div class="input-group">
                <span class="input-icon">
                  <mat-icon fontSet="material-symbols-rounded">lock</mat-icon>
                </span>
                <input id="confirmPassword"
                       [type]="showConfirmPassword ? 'text' : 'password'"
                       placeholder="Confirm new password"
                       formControlName="confirmPassword"
                       [class.is-invalid]="confirmPassword?.invalid && confirmPassword?.touched"
                       [disabled]="!isVerificationCodeAvailable() || isVerificationCodeExpired" />
                <button type="button"
                        class="eye-btn"
                        (click)="toggleConfirmPasswordVisibility()"
                        tabindex="-1"
                        [disabled]="!isVerificationCodeAvailable() || isVerificationCodeExpired">
                  <mat-icon fontSet="material-symbols-rounded">
                    {{ showConfirmPassword ? 'visibility' : 'visibility_off' }}
                  </mat-icon>
                </button>
              </div>
              <div *ngIf="confirmPassword?.invalid && confirmPassword?.touched" class="text-danger small">
                <div *ngIf="confirmPassword?.errors?.['required']">Confirm password is required</div>
                <div *ngIf="confirmPassword?.errors?.['minlength']">Password must be at least 6 characters</div>
              </div>
              <div *ngIf="forgotPasswordForm.errors?.['passwordMismatch'] && confirmPassword?.touched" class="text-danger small">
                <mat-icon fontSet="material-symbols-rounded" class="inline-icon">error</mat-icon>
                Passwords do not match
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <button class="login-btn forgot-password-btn"
                  type="submit"
                  [disabled]="forgotPasswordForm.invalid || isSubmitting || !isVerificationCodeAvailable() || isVerificationCodeExpired">
            <span *ngIf="!isSubmitting">Reset Password</span>
            <span *ngIf="isSubmitting">
              <i class="fas fa-spinner fa-spin"></i> Resetting...
            </span>
          </button>

          <!-- Back to Login -->
          <div class="back-link">
            <a (click)="onCancel()">
              <mat-icon fontSet="material-symbols-rounded">arrow_back</mat-icon>
              Back to Login
            </a>
          </div>
        </form>
      </div>
    </section>
  </div>
</div>
